import { useEffect, useState } from "react";
import { fetchSponsors } from "@/lib/sponsors";
import SponsorLogo from "./SponsorLogo";

/**
 * Generic horizontal strip of sponsor logos for a given tier.
 * `eyebrow` above, `cta` optional link below.
 */
export default function SponsorStrip({
  tier,
  eyebrow,
  tone = "light",
  cta,
  ctaHref,
  size = "md",
  centered = true,
  initialSponsors, // optional pre-fetched array
  className = "",
  testid,
}) {
  const [sponsors, setSponsors] = useState(initialSponsors || null);

  useEffect(() => {
    if (initialSponsors) return;
    let alive = true;
    fetchSponsors({ tier, status: "active,placeholder" })
      .then((list) => alive && setSponsors(list))
      .catch(() => alive && setSponsors([]));
    return () => {
      alive = false;
    };
  }, [tier, initialSponsors]);

  if (!sponsors || sponsors.length === 0) return null;

  const eyebrowColor = tone === "dark" ? "text-gold" : "text-terracotta";
  const ctaColor = tone === "dark" ? "text-gold hover:text-cream" : "text-terracotta hover:text-[#A64826]";

  return (
    <div data-testid={testid || `sponsor-strip-${tier}`} className={className}>
      {eyebrow && (
        <p className={`text-[11px] uppercase tracking-[0.28em] ${eyebrowColor} mb-5 ${centered ? "text-center" : ""}`}>
          {eyebrow}
        </p>
      )}
      <div className={`flex flex-wrap gap-8 md:gap-14 items-center ${centered ? "justify-center" : ""}`}>
        {sponsors.map((s) => (
          <SponsorLogo key={s.id} sponsor={s} tone={tone} size={size} />
        ))}
      </div>
      {cta && ctaHref && (
        <div className={`mt-5 ${centered ? "text-center" : ""}`}>
          <a href={ctaHref} className={`text-xs uppercase tracking-[0.2em] transition-colors ${ctaColor}`}>
            {cta} →
          </a>
        </div>
      )}
    </div>
  );
}
